import { Button, Center, Flex, Loader, Stack, TextInput } from '@mantine/core';
import { IconCheck, IconChevronLeft } from '@tabler/icons-react';
import { GetSensorResponse } from 'api/actions/get-sensor/get-sensor-response';
import useLang from 'lang';
import panic from 'panic';
import { withBrowserTimezone } from 'env/browser-timezone';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MinusIcon } from 'components/icons/MinusIcon';
import { PlusIcon } from 'components/icons/PlusIcon';
import { useApi } from 'api/api-context';
import P1Medium from 'theme/components/Typography/P1Medium';
import H5SemiBold from 'theme/components/Typography/H5SemiBold';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { UpdateSensorAlertSettingsPayload } from 'api/actions/update-sensor-alert-settings/update-sensor-alert-settings-payload';
import P2Medium from 'theme/components/Typography/P2Medium';
import theme from 'theme';
import { notifications } from '@mantine/notifications';
import { SENSOR_DETAIL_PAGE_PATH } from 'routes/paths';

type Sensor = GetSensorResponse['sensor'];
type SensorAlert = NonNullable<Sensor['alerts']>[number];

/**
 * Returns the English source label for known alert fields.
 * @param alert The alert that owns the field.
 * @param field The configurable alert field.
 */
const getAlertFieldLabelKey = (alert: SensorAlert, field: NonNullable<SensorAlert['fields']>[number]) => {
  if (alert.type === '24h-consumption' && field.name === 'limit') {
    return 'Daily consumption limit';
  }

  if (alert.type === 'lower-limit' && field.name === 'limit') {
    return 'Lower limit';
  }

  if (alert.type === 'upper-limit' && field.name === 'limit') {
    return 'Upper limit';
  }

  return field.label ?? '';
};

/**
 *
 * @returns The component tree for the alert settings page.
 */
export default function AlertSettingsPage() {
  const { _t } = useLang();
  const { sensorId } = useParams<{ sensorId: string }>();
  const navigate = useNavigate();
  const api = useApi();

  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [alertSettings, setAlertSettings] = useState<SensorAlert[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch sensor data
  useEffect(() => {
    if (!sensorId) return;

    /**
     * Fetches the sensor data.
     */
    const fetchSensor = async () => {
      try {
        const response = await api.getAction('GetSensor')({
          parameters: { sensorId: Number(sensorId) },
          query: withBrowserTimezone({}),
        });
        setSensor(response.sensor);

        if (response.sensor.alerts) {
          setAlertSettings(response.sensor.alerts.filter((alert) => alert.configurable !== false));
        }
      } catch (error) {
        panic(error as Error);
      }
    };

    fetchSensor();
  }, [api, sensorId]);

  /**
   * Updates the alert field.
   * @param alertType The type of the alert.
   * @param fieldName The name of the field.
   * @param value The value of the field.
   */
  const updateAlertField = (alertType: SensorAlert['type'], fieldName: string, value: number | null) => {
    setAlertSettings((prevSettings) => {
      return prevSettings.map((alert) => {
        if (alert.type === alertType) {
          return {
            ...alert,
            fields: (alert.fields ?? []).map((field) => {
              if (field.name === fieldName && field.type === 'number') {
                if (value === null) {
                  return {
                    ...field,
                    value: undefined,
                  };
                }

                const min = field.min ?? Number.NEGATIVE_INFINITY;
                const max = field.max ?? Number.POSITIVE_INFINITY;

                return {
                  ...field,
                  value: Math.min(Math.max(value, min), max),
                };
              }

              return field;
            }),
          };
        }
        return alert;
      });
    });
  };

  /**
   * Returns the step used by numeric alert field controls.
   * @param alert The alert that owns the field.
   * @param field The numeric field.
   */
  const getFieldStep = (alert: SensorAlert, field: NonNullable<SensorAlert['fields']>[number]) => {
    if (alert.type === '24h-consumption' && field.name === 'limit') {
      return 10;
    }

    return field.step ?? 1;
  };

  /**
   * Updates a numeric alert field from keyboard input.
   * @param alertType The type of the alert.
   * @param fieldName The field name.
   * @param rawValue The raw input value.
   */
  const handleNumberInputChange = (alertType: SensorAlert['type'], fieldName: string, rawValue: string) => {
    if (rawValue.trim() === '') {
      updateAlertField(alertType, fieldName, null);
      return;
    }

    const value = Number(rawValue);

    if (Number.isFinite(value)) {
      updateAlertField(alertType, fieldName, value);
    }
  };

  /**
   * Handles the save operation.
   */
  const handleSave = async () => {
    if (!sensorId || !sensor) return;

    const missingRequiredLimit = alertSettings.some((alert) => {
      if ((alert.type !== 'lower-limit' && alert.type !== 'upper-limit') || !alert.active) {
        return false;
      }

      const limitField = alert.fields?.find((field) => field.name === 'limit' && field.type === 'number');
      return limitField?.value === undefined || limitField.value === null;
    });

    if (missingRequiredLimit) {
      notifications.show({
        title: _t('Alert settings'),
        message: _t('Set a limit before enabling this alert.'),
      });
      return;
    }

    setIsSaving(true);

    try {
      // Transform alertSettings to the format expected by the API
      const payload: UpdateSensorAlertSettingsPayload = {
        alertSettings: alertSettings.flatMap((alert) => {
          const limitField = alert.fields?.find((field) => field.name === 'limit' && field.type === 'number');

          if (alert.type === '24h-consumption' && limitField?.type === 'number') {
            return [
              {
                type: alert.type,
                limit: limitField.value ?? 1000,
                enabled: alert.active ?? true,
              },
            ];
          }

          if (
            (alert.type === 'lower-limit' || alert.type === 'upper-limit') &&
            limitField?.type === 'number' &&
            limitField.value !== undefined &&
            limitField.value !== null
          ) {
            return [
              {
                type: alert.type,
                limit: limitField.value,
                enabled: alert.active ?? false,
              },
            ];
          }

          return [];
        }),
      };

      // Call the update API
      await api.getAction('UpdateSensorAlertSettings')({
        parameters: { sensorId: Number(sensorId) },
        payload,
      });

      navigate(SENSOR_DETAIL_PAGE_PATH.insert({ sensorId }));
    } catch (error) {
      panic(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!sensor) {
    return (
      <Center style={{ height: '100vh' }}>
        <Flex direction="column" align="center" gap="1rem">
          <Loader size="lg" color="#551FFF" />
          <H2SemiBold>{_t('Loading settings')}</H2SemiBold>
        </Flex>
      </Center>
    );
  }

  return (
    <Stack bg="gray.0" py={20} px={16} h="100vh">
      <Flex direction="column" gap={12} mb={6}>
        <Link
          to={SENSOR_DETAIL_PAGE_PATH.insert({ sensorId })}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', width: 'fit-content' }}
        >
          <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
          <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back')}</P2Medium>
        </Link>
        <span style={{ textAlign: 'center', width: '100%' }}>
          <H5SemiBold>{_t('Alert settings')}</H5SemiBold>
        </span>
      </Flex>
      <Flex direction="column" justify="space-between" h="100%">
        <Stack gap={0}>
          {alertSettings.map((alert) => (
            <Stack key={alert.type ?? alert.title} bg="white" p={16} style={{ borderRadius: '0.625rem' }} mb={20}>
              {(alert.fields ?? []).map((field) => (
                <Flex direction={'column'} key={field.name ?? field.label} mt="xs" w="100%">
                  {field.type === 'number' && (
                    <>
                      <P1Medium pb={16}>{_t(getAlertFieldLabelKey(alert, field))}</P1Medium>
                      <Flex py={16} gap={16} justify="space-between" align="center">
                        <span
                          onClick={() =>
                            updateAlertField(
                              alert.type,
                              field.name ?? '',
                              (field.value ?? 0) - getFieldStep(alert, field)
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <MinusIcon />
                        </span>
                        <TextInput
                          variant="tertiary"
                          radius={10}
                          styles={{
                            input: {
                              border: '1px solid #E0E4EC',
                              height: '48px',
                              textAlign: 'center',
                              fontSize: '18px',
                            },
                          }}
                          type="number"
                          inputMode="numeric"
                          min={field.min}
                          max={field.max}
                          step={getFieldStep(alert, field)}
                          value={field.value ?? ''}
                          rightSection={field.unit}
                          onChange={(event) =>
                            handleNumberInputChange(alert.type, field.name ?? '', event.currentTarget.value)
                          }
                        />
                        <span
                          onClick={() =>
                            updateAlertField(
                              alert.type,
                              field.name ?? '',
                              (field.value ?? 0) + getFieldStep(alert, field)
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <PlusIcon />
                        </span>
                      </Flex>
                    </>
                  )}
                </Flex>
              ))}
            </Stack>
          ))}
        </Stack>

        <Flex direction="column" gap="md">
          <Button
            variant="primary"
            size="md"
            leftSection={isSaving ? <Loader size="sm" color="white" /> : <IconCheck size={24} />}
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving}
          >
            {isSaving ? _t('Saving...') : _t('Save')}
          </Button>
        </Flex>
      </Flex>
    </Stack>
  );
}
